import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { ChangePackageSummaryVm } from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';
import { getChangePackageStatusLabel } from '@/features/dashboard/bookingChanges/types/ChangeViewModel.utils.ts';
import { ChangePackageStep } from '@/features/dashboard/bookingChanges/routes/changePackageSearch.ts';
import { Card } from '@/components/ui/Card.tsx';
import { ScreenTitle } from '@/components/ui/Title.tsx';
import MoneyAmountList from '@/features/dashboard/bookingChanges/components/primitives/MoneyAmountList.tsx';
import {
  ChangePackageStepBadge,
  ChangeSummaryTable,
} from '@/features/dashboard/bookingChanges/components/flow';
import {
  balanceLabel,
  packageContextLabel,
  settlementIntroSentence,
  summaryScopeSentence,
} from '@/features/dashboard/bookingChanges/components/flow/changePackageCopy.ts';
import { summaryStepGuard } from '@/features/dashboard/bookingChanges/components/flow/changePackageSteps.ts';

interface ChangePackageSummaryStepProps
  extends React.HTMLAttributes<HTMLDivElement> {
  summary: ChangePackageSummaryVm;
  /** „Wróć do edycji” — powrót na ekran rezerwacji w trybie zmiany. */
  onBack: () => void;
  /** Otwarcie rezerwacji z linku w zdaniu opisu. */
  onOpenBooking: () => void;
  onNext: () => void;
}

/**
 * Krok W1 — podsumowanie tego, co zmienia szkic.
 *
 * Bezstanowy: wszystko, co pokazuje, przychodzi w `summary`, a każde kliknięcie
 * wychodzi zdarzeniem. Opis nad tabelą jest liczony z danych, więc nie może
 * rozjechać się z listą pozycji pod nim.
 */
const ChangePackageSummaryStep = ({
  summary,
  onBack,
  onOpenBooking,
  onNext,
  className,
  ...props
}: ChangePackageSummaryStepProps) => {
  const guard = summaryStepGuard(summary);

  return (
    <Card
      highlighted
      className={cn('@container overflow-hidden', className)}
      {...props}
    >
      <div className="@min-[700px]:px-7 border-b border-neutral-100 px-4 pb-5 pt-[22px]">
        <ChangePackageStepBadge
          step={ChangePackageStep.SUMMARY}
          context={packageContextLabel(
            summary.packageNumber,
            getChangePackageStatusLabel(summary.status),
          )}
        />
        <ScreenTitle className="mt-2.5">Podsumowanie zmian</ScreenTitle>
        <p className="mt-1.5 max-w-[620px] text-pretty text-[15px] text-neutral-600">
          {summaryScopeSentence(summary.itemCount, summary.financialItemCount)}{' '}
          Nic nie jest jeszcze wprowadzone — rezerwacja{' '}
          <button
            type="button"
            onClick={onOpenBooking}
            className="font-semibold text-info-300 underline-offset-2 hover:underline"
          >
            {summary.bookingCode}
          </button>{' '}
          działa na wartościach sprzed zmian.
        </p>
      </div>

      <ChangeSummaryTable groups={summary.groups} />

      <div className="@min-[700px]:px-7 flex flex-wrap items-baseline gap-x-3.5 gap-y-1 bg-neutral-50 px-4 py-4">
        <span className="text-[15px] font-bold text-ink">Bilans paczki</span>
        <span className="text-sm text-neutral-500">
          {balanceLabel(summary.balance)}
        </span>
        <span className="@max-[700px]:w-full @max-[700px]:text-right ml-auto text-2xl font-extrabold tabular-nums tracking-[-.02em] text-ink">
          <MoneyAmountList values={summary.balance} variant="signed" />
        </span>
      </div>

      <div className="@max-[700px]:flex-col @max-[700px]:items-stretch @min-[700px]:px-7 flex items-center gap-3.5 px-4 pb-[22px] pt-[18px]">
        <span className="min-w-0 max-w-[560px] text-pretty text-sm text-neutral-600">
          {settlementIntroSentence(summary.balance)}
        </span>
        <div className="@max-[700px]:ml-0 @max-[700px]:flex-col ml-auto flex w-full justify-end gap-2.5">
          <ActionButton
            tone="outline"
            className={'@max-[700px]:flex-1'}
            onClick={onBack}
          >
            Wróć do edycji
          </ActionButton>
          <ActionButton
            tone="accent"
            onClick={onNext}
            className={'@max-[700px]:flex-1'}
            disabled={!guard.canProceed}
            title={guard.reason}
          >
            Dalej: rozliczenie
            <ArrowRight aria-hidden />
          </ActionButton>
        </div>
      </div>
    </Card>
  );
};

export default ChangePackageSummaryStep;
